import { planAtLeast } from "@/lib/plans";
import { getMyTrial } from "@/lib/trial.functions";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ChevronDown,
  Crown,
  Film,
  ImageOff,
  Images,
  Loader2,
  Gem,
  Check,
  Layers,
  Pencil,
  Search,
  Sparkles,
  SquareCheckBig,
  Trash2,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VideoScanner } from "@/components/scan/VideoScanner";
import { DeepValuationDialog } from "@/components/scan/DeepValuationDialog";
import { ScanCompleteCard } from "@/components/scan/ScanCompleteCard";
import { ScanRunDebugPanel } from "@/components/scan/ScanRunDebugPanel";

import { PhotoScanner } from "@/components/scan/PhotoScanner";
import { ScanModePicker } from "@/components/scan/ScanModePicker";
import { scanMode } from "@/lib/scan-modes";
import { SoldPriceDialog } from "@/components/scan/SoldPriceDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getValuationQuota } from "@/lib/deep-valuation.functions";

import { getMyPlanStatus } from "@/lib/plans.functions";
import { myRoleQuery } from "@/lib/queries";
import {
  deleteScanItem,
  deleteScanItems,
  deleteScanRun,
  mergeScanItems,
  listMyScans,
  renameScanRun,
  updateScanItem,
  getScanRunPrefs,
  setScanRunPref,
} from "@/lib/scan-runs.functions";
import { cn } from "@/lib/utils";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";

export const Route = createFileRoute("/_authenticated/mina-prylar")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { skanning?: string; lage?: string; start?: number } => ({
    ...(typeof search["skanning"] === "string" ? { skanning: search["skanning"] } : {}),
    ...(search["lage"] === "film" || search["lage"] === "foton" || search["lage"] === "djup"
      ? { lage: search["lage"] as string }
      : {}),
    ...(search["start"] === 1 || search["start"] === "1" ? { start: 1 } : {}),
  }),

  head: () => ({
    meta: [
      { title: "Mina prylar — filma, värdera och sälj | Sellavo" },
      {
        name: "description",
        content:
          "Filma ett rum, vi listar prylarna med uppskattat värde och behåll listan här — med bild från exakt den sekund prylen syns.",
      },
      { property: "og:title", content: "Mina prylar — filma, värdera och sälj" },
      {
        property: "og:description",
        content:
          "Skanna ett rum med video och få alla dina prylar samlade med bild och uppskattat andrahandsvärde.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MyThingsPage,
});

const CONFIDENCE: Record<"low" | "medium" | "high", { label: string; className: string }> = {
  low: { label: "Osäker", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  medium: { label: "Ganska säker", className: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
  high: { label: "Säker", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
};

const STATUS: Record<"kept" | "sold" | "listed" | "hidden", string> = {
  kept: "Behåller",
  sold: "Såld",
  listed: "Annonserad",
  hidden: "Dold",
};

function kr(value: number) {
  return `${value.toLocaleString("sv-SE")} kr`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDuration(seconds: number) {
  const s = Math.round(seconds);
  if (!s) return null;
  return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;
}

type Item = Awaited<ReturnType<typeof listMyScans>>["items"][number];

function MyThingsPage() {
  const { enabled: testFeatures } = useExpertFeatures();
  const fetchScans = useServerFn(listMyScans);
  const rename = useServerFn(renameScanRun);
  const removeRun = useServerFn(deleteScanRun);
  const removeItem = useServerFn(deleteScanItem);
  const removeItems = useServerFn(deleteScanItems);
  const mergeItems = useServerFn(mergeScanItems);
  const patchItem = useServerFn(updateScanItem);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: ["my-scans"],
    queryFn: () => fetchScans({}),
  });

  const { data: planStatus, isPending: planPending } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
  });
  const isPro = planAtLeast(planStatus?.plan, "pro");
  const canPhotoScan = planAtLeast(planStatus?.plan, "plus");
  const isExpert = planAtLeast(planStatus?.plan, "expert");

  // Gratisprovet: en videoskanning och en fotoskanning på huset.
  const { data: trial } = useQuery({ queryKey: ["my-trial"], queryFn: () => getMyTrial({}) });
  const trialVideo = !isPro && (trial?.videoScansLeft ?? 0) > 0;
  const trialPhotos = !canPhotoScan && (trial?.photoScansLeft ?? 0) > 0;
  const canVideoScan = isPro || trialVideo;
  const canPhotos = canPhotoScan || trialPhotos;

  // Hur många djupskanningar som är kvar den här perioden – visas överallt vi erbjuder den.
  const { data: valuationQuota } = useQuery({
    queryKey: ["valuation-quota"],
    queryFn: () => getValuationQuota(),
  });
  const deepRemainingLabel =
    valuationQuota == null
      ? null
      : valuationQuota.limit === null
        ? "Obegränsat"
        : `${valuationQuota.remaining ?? 0} av ${valuationQuota.limit} kvar`;

  // Debugpanelen per skanning är bara till för admin och superadmin.
  const { data: roleData } = useQuery(myRoleQuery());
  const isAdminViewer = roleData?.role === "admin" || roleData?.role === "superadmin";

  const [query, setQuery] = useState("");
  // Djupskanning som startar med en helt ny pryl: vi kör en liten fotoskanning
  // och öppnar sedan djupskanningen på prylen som skapades.
  const [deepNewPhoto, setDeepNewPhoto] = useState(false);
  const [pendingDeepRunId, setPendingDeepRunId] = useState<string | null>(null);
  const [sort, setSort] = useState<"value" | "new" | "name">("value");
  const [openRuns, setOpenRuns] = useState<Record<string, boolean>>({});
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);
  const [activeMode, setActiveMode] = useState<"video" | "photos" | "deep" | null>(null);
  const scannerOpen = activeMode === "video";
  const photoOpen = activeMode === "photos";
  const deepOpen = activeMode === "deep";
  const [pendingSold, setPendingSold] = useState<Item | null>(null);

  const [justSavedRunId, setJustSavedRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Item | null>(null);
  const [valuating, setValuating] = useState<{ id: string; name: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Item | null>(null);
  const [pendingRunDelete, setPendingRunDelete] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number; name: string } | null>(
    null,
  );
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [pendingMerge, setPendingMerge] = useState(false);

  const isSelected = (id: string) => selected.includes(id);
  const toggleSelected = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  const clearSelection = () => setSelected([]);

  const hideBrokenImage = (url: string) => {
    setBrokenImages((current) => (current[url] ? current : { ...current, [url]: true }));
  };

  // Sparat utfällt/ihopfällt läge per skanning – följer med mellan enheter.
  const { data: runPrefs } = useQuery({
    queryKey: ["scan-run-prefs"],
    queryFn: () => getScanRunPrefs(),
  });
  useEffect(() => {
    if (!runPrefs?.prefs) return;
    setOpenRuns((current) => ({ ...runPrefs.prefs, ...current }));
  }, [runPrefs]);
  const prefMutation = useMutation({
    mutationFn: (input: { runId: string; expanded: boolean }) => setScanRunPref({ data: input }),
  });
  const toggleRun = (runId: string, next: boolean) => {
    setOpenRuns((o) => ({ ...o, [runId]: next }));
    prefMutation.mutate({ runId, expanded: next });
  };

  // Notiser och toasts länkar hit med ?skanning=<id> – då fäller vi ut just den
  // skanningen och scrollar dit.
  const { skanning, lage, start } = Route.useSearch();
  const autoStart = start === 1;
  const scrolledRef = useRef<string | null>(null);
  const runRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const openRun = (runId: string) => {
    setOpenRuns((o) => ({ ...o, [runId]: true }));
    prefMutation.mutate({ runId, expanded: true });
    setJustSavedRunId(runId);
    let tries = 0;
    const tick = window.setInterval(() => {
      tries += 1;
      const el = runRefs.current[runId];
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
        window.clearInterval(tick);
      } else if (tries > 20) {
        window.clearInterval(tick);
      }
    }, 100);
  };
  useEffect(() => {
    if (!skanning || scrolledRef.current === skanning) return;
    if (!(data?.runs ?? []).some((r) => r.id === skanning)) return;
    scrolledRef.current = skanning;
    openRun(skanning);
  }, [skanning, data?.runs]);

  // ?lage=film / ?lage=foton öppnar rätt skanner direkt (t.ex. från mobilmenyn).
  const modeFromUrlRef = useRef(false);
  useEffect(() => {
    if (!lage || modeFromUrlRef.current) return;
    modeFromUrlRef.current = true;
    setActiveMode(lage === "foton" ? "photos" : lage === "djup" && testFeatures ? "deep" : "video");
  }, [lage, testFeatures]);






  // Fäll ut och scrolla fram rätt skanner när man väljer läge.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!activeMode) return;
    const id = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(id);
  }, [activeMode]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["my-scans"] });

  const renameMutation = useMutation({
    mutationFn: (input: { runId: string; label: string }) => rename({ data: input }),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
    onError: () => toast.error("Kunde inte byta namn."),
  });

  const deleteRunMutation = useMutation({
    mutationFn: (runId: string) => removeRun({ data: { runId } }),
    onSuccess: () => {
      toast.success("Skanningen är raderad.");
      refresh();
    },
    onError: () => toast.error("Kunde inte radera skanningen."),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => removeItem({ data: { itemId } }),
    onSuccess: refresh,
    onError: () => toast.error("Kunde inte radera prylen."),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (itemIds: string[]) => removeItems({ data: { itemIds } }),
    onSuccess: (result) => {
      toast.success(
        `${result.removed} ${result.removed === 1 ? "pryl borttagen" : "prylar borttagna"}.`,
      );
      clearSelection();
      setPendingBulkDelete(false);
      refresh();
    },
    onError: () => toast.error("Kunde inte ta bort prylarna."),
  });

  const mergeMutation = useMutation({
    mutationFn: (itemIds: string[]) => mergeItems({ data: { itemIds } }),
    onSuccess: (result) => {
      toast.success(`${result.mergedCount + 1} prylar är nu en enda pryl.`);
      clearSelection();
      setPendingMerge(false);
      refresh();
    },
    onError: () => toast.error("Kunde inte slå ihop prylarna."),
  });

  const statusMutation = useMutation({
    mutationFn: (input: {
      itemId: string;
      status: "kept" | "sold" | "listed" | "hidden";
      soldPrice?: number | null;
    }) =>
      patchItem({ data: input }),
    onSuccess: refresh,
    onError: () => toast.error("Kunde inte uppdatera prylen."),
  });






  const runs = data?.runs ?? [];
  const items = useMemo(() => data?.items ?? [], [data]);

  // När fotoskanningen inne i djupskanningen sparat prylen öppnar vi
  // djupskanningen direkt på den nya prylen.
  useEffect(() => {
    if (!pendingDeepRunId) return;
    const fresh = items.filter((i) => i.runId === pendingDeepRunId);
    if (!fresh.length) return;
    setPendingDeepRunId(null);
    setDeepNewPhoto(false);
    setValuating({ id: fresh[0].id, name: fresh[0].name });
  }, [pendingDeepRunId, items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? items.filter((i) =>
          `${i.name} ${i.descriptor} ${i.note}`.toLowerCase().includes(q),
        )
      : [...items];
    list.sort((a, b) => {
      if (sort === "value") return b.valueHigh - a.valueHigh;
      if (sort === "name") return a.name.localeCompare(b.name, "sv");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return list;
  }, [items, query, sort]);

  const totals = useMemo(
    () =>
      items.reduce(
        (acc, i) => ({ low: acc.low + i.valueLow, high: acc.high + i.valueHigh }),
        { low: 0, high: 0 },
      ),
    [items],
  );

  /** Tar med prylen till annonsflödet som förslag på text och pris. */
  function sellItem(item: Item) {
    const suggested = Math.round((item.valueLow + item.valueHigh) / 2);
    try {
      sessionStorage.setItem(
        "sellavo.scan-prefill",
        JSON.stringify({
          name: item.name,
          descriptor: item.descriptor,
          note: item.note,
          category: item.category,
          price: suggested,
          imageUrls: item.imageUrls,
          jump: true,
        }),

      );
    } catch {
      // Utan sessionStorage får man fylla i själv.
    }
    // Stäng prylvyn och gå hela vägen in i annonsflödet innan vi uppdaterar
    // listan i bakgrunden — annars hinner listan rendera om och sidan blinkar.
    setDetail(null);
    void navigate({ to: "/skapa", replace: false }).then(() => {
      statusMutation.mutate({ itemId: item.id, status: "listed" });
    });
  }

  /** Kompakt kort: bild, namn och uppskattat pris. Hela kortet öppnar prylen. */
  function ItemCard({ item, showRun }: { item: Item; showRun?: boolean }) {
    const run = runs.find((r) => r.id === item.runId);
    const price =
      item.valueLow === item.valueHigh
        ? kr(item.valueHigh)
        : `${item.valueLow.toLocaleString("sv-SE")}–${kr(item.valueHigh)}`;
    return (
      <li className={item.status === "sold" || item.status === "hidden" ? "opacity-60" : ""}>
        <button
          type="button"
          onClick={() => (selected.length ? toggleSelected(item.id) : setDetail(item))}
          className={cn(
            "grid w-full grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-3 p-4 text-left transition-colors hover:bg-muted/40 sm:gap-4",
            isSelected(item.id) ? "bg-primary/5" : "",
          )}
        >
          <span
            role="checkbox"
            aria-checked={isSelected(item.id)}
            aria-label={`Markera ${item.name}`}
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelected(item.id);
            }}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                toggleSelected(item.id);
              }
            }}
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
              isSelected(item.id)
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background",
            )}
          >
            {isSelected(item.id) && <Check className="h-3.5 w-3.5" />}
          </span>
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-muted sm:h-20 sm:w-20">
            {item.thumbUrl && !brokenImages[item.thumbUrl] ? (
              <img
                src={item.thumbUrl}
                alt={`Bild på ${item.name}`}
                loading="lazy"
                className="h-full w-full object-cover"
                onError={() => hideBrokenImage(item.thumbUrl ?? "")}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <ImageOff className="h-5 w-5" />
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className="truncate font-medium">
              {item.name}
              {item.quantity > 1 ? ` ×${item.quantity}` : ""}
            </p>
            <p className="mt-1 block font-heading text-lg font-semibold leading-tight text-foreground sm:text-xl">
              {price}
            </p>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {showRun && run ? `${run.label} · ` : ""}
              {item.status !== "kept" ? `${STATUS[item.status]} · ` : ""}
              {item.imageUrls.length > 1 ? `${item.imageUrls.length} foton` : "1 foto"}
            </p>
            {item.valuationCount > 0 && (
              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                <Gem className="h-3 w-3" />
                Djupvärderad
                {item.valuationValueMid != null ? ` · ${kr(item.valuationValueMid)}` : ""}
              </p>
            )}
            {item.valuationMaker && (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                Trolig upphovsperson: {item.valuationMaker}
              </p>
            )}
          </div>
        </button>
      </li>
    );
  }

  /** Prylvy: bilder, redigerbart namn och värde samt de självklara knapparna. */
  function ItemDialog() {
    const item = detail ? (items.find((i) => i.id === detail.id) ?? detail) : null;
    if (!item) return null;
    return (
      <Dialog open onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="pr-6 text-left">{item.name}</DialogTitle>
          </DialogHeader>

          {(() => {
            // Bilden ur filmen ligger först, sedan eventuella extra foton.
            const urls = Array.from(
              new Set([item.thumbUrl, ...item.imageUrls].filter((u): u is string => !!u)),
            ).filter((url) => !brokenImages[url]);
            if (!urls.length) return null;
            return (
              <div>
                <button
                  type="button"
                  onClick={() => setLightbox({ urls, index: 0, name: item.name })}
                  className="block w-full overflow-hidden rounded-xl border border-border bg-muted"
                >
                  <img
                    src={urls[0]}
                    alt={`Bild på ${item.name}`}
                    className="h-56 w-full object-cover"
                    onError={() => hideBrokenImage(urls[0] ?? "")}
                  />
                </button>
                {urls.length > 1 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                    {urls.slice(1).map((url, i) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setLightbox({ urls, index: i + 1, name: item.name })}
                        className="shrink-0 overflow-hidden rounded-lg border border-border"
                      >
                        <img
                          src={url}
                          alt={`Bild ${i + 2} på ${item.name}`}
                          loading="lazy"
                          className="h-20 w-20 object-cover"
                          onError={() => hideBrokenImage(url)}
                        />
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Tryck på bilden för att se den stort.
                </p>
              </div>
            );
          })()}

          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Uppskattat värde</p>
              <p className="font-heading text-xl">
                {item.valueLow === item.valueHigh
                  ? kr(item.valueHigh)
                  : `${item.valueLow.toLocaleString("sv-SE")}–${kr(item.valueHigh)}`}
              </p>
            </div>
            {(item.descriptor || item.note) && (
              <p className="text-sm text-muted-foreground">
                {[item.descriptor, item.note].filter(Boolean).join(" · ")}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {CONFIDENCE[item.confidence].label}
              {item.atSecond != null
                ? ` · Syns vid ${item.atSecond.toFixed(1).replace(".", ",")} sek`
                : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              Namn och pris justerar du när du skapar annonsen.
            </p>
          </div>


          {item.valuationCount > 0 && (
            <div className="mt-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
              <p className="text-xs font-medium text-primary">
                Djupvärdering sparad
                {item.valuationAt
                  ? ` ${new Date(item.valuationAt).toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" })}`
                  : ""}
              </p>
              <p className="mt-0.5 text-sm">
                {item.valuationValueMid != null
                  ? `Marknadsvärde ${kr(item.valuationValueMid)} — närbilder, källor och motivering finns kvar.`
                  : "Närbilder, källor och motivering finns kvar."}
              </p>
            </div>
          )}

          {isPro ? (
            <Button
              variant="outline"
              className="mt-2 w-full justify-start gap-2"
              onClick={() => setValuating({ id: item.id, name: item.name })}
            >
              <Gem className="h-4 w-4 text-primary" />
              <span className="flex-1 text-left">
                {item.valuationCount > 0
                  ? `Se djupskanningen${item.valuationCount > 1 ? ` (${item.valuationCount})` : ""}`
                  : "Djupskanna prylen"}
              </span>
              {deepRemainingLabel && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {deepRemainingLabel}
                </span>
              )}
            </Button>
          ) : (
            <Link
              to="/priser"
              onClick={() => setDetail(null)}
              className="mt-2 flex w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/40"
            >
              <Gem className="h-4 w-4 text-primary" />
              Djupskanning med följdfrågor och marknadsunderlag ingår i Pro
            </Link>
          )}

          <div className="mt-2 flex flex-wrap gap-2">
            <Button className="flex-1" onClick={() => sellItem(item)}>
              Skapa annons
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (item.status === "sold") {
                  statusMutation.mutate({ itemId: item.id, status: "kept" });
                } else {
                  setPendingSold(item);
                }
              }}
            >
              {item.status === "sold" ? "Ångra såld" : "Markera såld"}
            </Button>
            <Button
              variant="ghost"
              aria-label="Ta bort prylen"
              className="text-muted-foreground"
              onClick={() => setPendingDelete(item)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <header className="mb-6">
        <h1 className="font-heading text-3xl">Mina prylar</h1>
        <p className="mt-2 text-muted-foreground">
          Filma ett rum i upp till 60 sekunder eller ladda upp en hög med foton — vi listar
          prylarna med uppskattat värde och allt sparas här, med bild på varje sak. Filmen raderas
          efter skanningen, listan blir kvar.
        </p>
      </header>

      <div className="mb-6">
        <ScanCompleteCard
          run={runs[0] ?? null}
          previewItems={items
            .filter((i) => i.runId === runs[0]?.id)
            .slice(0, 6)
            .map((i) => ({ id: i.id, name: i.name, thumbUrl: i.thumbUrl }))}
          onOpen={openRun}
        />

      </div>

      {/* Ett samlat val: filma ett rum eller ladda upp en hög med foton. */}
      <section className="mb-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg">Skanna dina prylar</h2>
          {!!items.length && (
            <p className="text-sm text-muted-foreground">
              {items.length} prylar sparade · {totals.low.toLocaleString("sv-SE")}–{kr(totals.high)}
            </p>
          )}
        </div>

        <ScanModePicker
          variant="rows"
          modes={testFeatures ? ["video", "photos", "deep"] : ["video", "photos"]}
          activeId={activeMode}
          isLocked={(mode) =>
            !planPending &&
            (mode.id === "video" ? !canVideoScan : mode.id === "deep" ? !isPro : !canPhotos)
          }
          noteFor={(mode) => (mode.id === "deep" ? deepRemainingLabel : null)}
          onSelect={(mode) =>
            setActiveMode((current) =>
              current === mode.id ? null : (mode.id as "video" | "photos" | "deep"),
            )
          }
        />

        {activeMode && (
          <div
            ref={panelRef}
            className="mt-5 scroll-mt-24 overflow-hidden rounded-2xl border border-primary/30 bg-primary/[0.04] ring-1 ring-primary/10"
          >
            <div className="flex items-center gap-3 border-b border-primary/20 bg-primary/[0.06] p-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                {(() => {
                  const Icon = scanMode(activeMode).icon;
                  return <Icon className="h-5 w-5" />;
                })()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-base font-semibold leading-tight">
                    {scanMode(activeMode).title}
                  </span>
                  {scanMode(activeMode).planLabel && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      {scanMode(activeMode).planLabel}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  {scanMode(activeMode).description}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 gap-2"
                onClick={() => setActiveMode(null)}
              >
                <X className="h-4 w-4" />
                Stäng
              </Button>
            </div>
            <div className="p-4 sm:p-5">

            {((scannerOpen && trialVideo) || (photoOpen && trialPhotos)) && (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/10 p-4">
                <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="text-sm">
                  <p className="font-semibold">Gratis att testa – en gång</p>
                  <p className="mt-0.5 text-muted-foreground">
                    {scannerOpen
                      ? "Du får göra en hel videoskanning på huset. Sedan ingår videoskanning i Pro."
                      : `Du får göra en hel fotoskanning på huset, med upp till ${trial?.photoScanImages ?? 10} bilder. Sedan ingår fotoskanning i Plus och Pro.`}
                  </p>
                </div>
              </div>
            )}

            {scannerOpen &&
              (canVideoScan ? (
                <VideoScanner
                  autoStart={autoStart}
                  onSaved={(runId) => {
                    setJustSavedRunId(runId);
                    setOpenRuns((o) => ({ ...o, [runId]: true }));
                    refresh();
                  }}
                />
              ) : (
                <div className="rounded-xl border border-border bg-muted/40 p-6 text-center">
                  <Crown className="mx-auto h-8 w-8 text-primary" />
                  <h3 className="mt-3 font-heading text-lg">Videoskanning ingår i Pro</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    Filma ett rum i upp till en minut så listar vi alla prylar vi hittar — varje
                    sak en gång — med ett uppskattat andrahandsvärde. Perfekt inför flytt, dödsbo
                    eller storstädning.
                  </p>
                  <Button asChild className="mt-4">
                    <Link to="/priser">Uppgradera till Pro</Link>
                  </Button>
                </div>
              ))}

            {photoOpen &&
              (canPhotos ? (
                <PhotoScanner
                  autoStart={autoStart}
                  onSaved={(runId) => {
                    setJustSavedRunId(runId);
                    setOpenRuns((o) => ({ ...o, [runId]: true }));
                    refresh();
                  }}
                />
              ) : (
                <div className="rounded-xl border border-border bg-muted/40 p-6 text-center">
                  <Crown className="mx-auto h-8 w-8 text-primary" />
                  <h3 className="mt-3 font-heading text-lg">
                    Fotoskanning ingår i Plus och Pro
                  </h3>
                  <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    Gå runt och fota dina prylar — flera bilder på samma sak går bra. Vi grupperar
                    ihop bilderna, sätter namn och uppskattar värdet, så du kan göra annonser
                    direkt.
                  </p>
                  <Button asChild className="mt-4">
                    <Link to="/priser">Se planerna</Link>
                  </Button>
                </div>
              ))}

            {testFeatures && deepOpen &&
              (isPro ? (
                <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-muted/30 p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="min-w-0 font-medium">Välj vilken pryl vi ska djupskanna</p>
                    {deepRemainingLabel && (
                      <span className="rounded-full bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {deepRemainingLabel}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Vi ställer följdfrågor, ber om närbilder på märkning och skick och jämför med
                    verkliga slutpriser. Resultatet blir ett väl underbyggt prisintervall med
                    källor och en färdig annonstext.
                  </p>
                  {deepNewPhoto ? (
                    <div className="mt-4">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="min-w-0 text-sm font-medium">
                          Fota prylen — sedan startar djupskanningen automatiskt
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDeepNewPhoto(false);
                            setPendingDeepRunId(null);
                          }}
                        >
                          Avbryt
                        </Button>
                      </div>
                      <PhotoScanner
                        autoStart
                        onSaved={(runId) => {
                          setPendingDeepRunId(runId);
                          refresh();
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeepNewPhoto(true)}
                      className="mt-4 flex w-full items-center gap-3 rounded-lg border border-dashed border-primary/40 bg-background p-3 text-left transition-colors hover:border-primary"
                    >
                      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                        <Images className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">Fota en ny pryl</span>
                        <span className="block text-xs text-muted-foreground">
                          Ta ett foto här och nu så djupskannar vi den direkt
                        </span>
                      </span>
                    </button>
                  )}

                  {deepNewPhoto ? null : items.length ? (
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {[...items]
                        .sort((a, b) => (a.valuationCount ? 1 : 0) - (b.valuationCount ? 1 : 0))
                        .slice(0, 8)
                        .map((item) => (
                          <li key={item.id}>
                            <button
                              type="button"
                              onClick={() => setValuating({ id: item.id, name: item.name })}
                              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/40"
                            >
                              <span className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                                {item.thumbUrl && !brokenImages[item.thumbUrl] ? (
                                  <img
                                    src={item.thumbUrl}
                                    alt={`Bild på ${item.name}`}
                                    loading="lazy"
                                    className="h-full w-full object-cover"
                                    onError={() => hideBrokenImage(item.thumbUrl ?? "")}
                                  />
                                ) : (
                                  <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                                    <ImageOff className="h-4 w-4" />
                                  </span>
                                )}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">
                                  {item.name}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {[item.category, item.descriptor].filter(Boolean).join(" · ") ||
                                    "Okänd kategori"}
                                </span>
                                {item.valuationCount > 0 && (
                                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                                    <Gem className="h-3 w-3" />
                                    Redan djupskannad
                                    {item.valuationValueMid != null
                                      ? ` · ${kr(item.valuationValueMid)}`
                                      : ""}
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-sm text-muted-foreground">
                      Du behöver en pryl först — gör en videoskanning eller fota några prylar.
                    </p>
                  )}
                  {items.length > 8 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Fler prylar finns i listan nedan — öppna en pryl och välj Djupskanna prylen.
                    </p>
                  )}

                </div>
              ) : (
                <div className="rounded-xl border border-border bg-muted/40 p-6 text-center">
                  <Crown className="mx-auto h-8 w-8 text-primary" />
                  <h3 className="mt-3 font-heading text-lg">Djupskanning ingår i Pro och Expert</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    En enskild pryl granskas på djupet: exakt modell, skick och verkliga slutpriser
                    blir ett väl underbyggt prisintervall med källor.
                  </p>
                  <Button asChild className="mt-4">
                    <Link to="/priser">Se planerna</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !items.length ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <Film className="mx-auto h-10 w-10 text-primary" />
          <h2 className="mt-4 font-heading text-xl">Inga prylar sparade än</h2>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            Filma ett rum eller ladda upp en hög med foton — varje pryl får en bild och ett
            uppskattat värde, och kan göras till annons med ett klick.
          </p>
          {!activeMode && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button className="gap-2" onClick={() => setActiveMode("video")}>
                <Video className="h-4 w-4" />
                Skanna en film
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => setActiveMode("photos")}>
                <Images className="h-4 w-4" />
                Fota flera prylar
              </Button>
            </div>
          )}
        </div>
      ) : (

        <>
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm">
                <strong>{items.length}</strong> prylar i{" "}
                <strong>{runs.length}</strong> {runs.length === 1 ? "skanning" : "skanningar"}
              </p>
              <p className="text-sm text-muted-foreground">
                Totalt{" "}
                <strong className="text-foreground">
                  {totals.low.toLocaleString("sv-SE")}–{kr(totals.high)}
                </strong>
              </p>
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Sök bland dina prylar"
                  className="pl-9"
                />
              </div>
              <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
                <SelectTrigger className="sm:w-52" aria-label="Sortera">
                  <SelectValue placeholder="Sortera" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="value">Högst värde</SelectItem>
                  <SelectItem value="new">Senast tillagda</SelectItem>
                  <SelectItem value="name">Namn A–Ö</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          {!!query.trim() && (
            <section className="mt-6">
              <h2 className="mb-3 font-heading text-xl">Sökträffar</h2>
              {filtered.length === 0 ? (
                <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-muted-foreground">
                  Inget matchade sökningen.
                </p>
              ) : (
                <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                  {filtered.map((item) => (
                    <ItemCard key={item.id} item={item} showRun />
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="mt-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-xl">Prylar per skanning</h2>
              <p className="text-sm text-muted-foreground">
                Markera flera för att ta bort eller slå ihop dubbletter.
              </p>
            </div>
            <div className="space-y-3">
              {runs.map((run) => {
                const runItems = items.filter((i) => i.runId === run.id);
                const open = openRuns[run.id] ?? true;
                const runItemIds = runItems.map((i) => i.id);
                const allSelected =
                  runItemIds.length > 0 && runItemIds.every((id) => selected.includes(id));
                const duration = formatDuration(run.durationSeconds);
                const isNew = run.id === justSavedRunId;
                const runThumbs = runItems
                  .map((i) => ({
                    id: i.id,
                    name: i.name,
                    url: i.thumbUrl ?? i.imageUrls[0] ?? null,
                  }))
                  .filter(
                    (t): t is { id: string; name: string; url: string } =>
                      !!t.url && !brokenImages[t.url],
                  )
                  .slice(0, 5);
                return (
                  <div
                    key={run.id}
                    ref={(el) => {
                      runRefs.current[run.id] = el;
                    }}
                    className={cn(
                      "overflow-hidden rounded-xl border bg-card",
                      isNew ? "border-primary ring-1 ring-primary/30" : "border-border",
                    )}
                  >
                    <div className="flex items-center gap-2 p-4">
                      <button
                        type="button"
                        onClick={() => toggleRun(run.id, !open)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        aria-expanded={open}
                      >
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                            open ? "rotate-180" : "",
                          )}
                        />
                        <span className="min-w-0">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate font-medium">{run.label}</span>
                            {isNew && (
                              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold leading-none text-primary">
                                Ny
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {formatDate(run.createdAt)}
                            {duration ? ` · ${duration}` : ""} · {runItems.length}{" "}
                            {runItems.length === 1 ? "pryl" : "prylar"}
                          </span>
                          <span className="mt-0.5 block font-heading text-base font-semibold leading-tight text-foreground">
                            {run.valueLow.toLocaleString("sv-SE")}–{kr(run.valueHigh)}
                          </span>
                          {/* Smygtitt på prylarna när raden är ihopfälld. */}
                          {!open && runThumbs.length > 0 && (
                            <span className="mt-2 flex items-center gap-1.5">
                              {runThumbs.map((thumb) => (
                                <img
                                  key={thumb.id}
                                  src={thumb.url}
                                  alt={thumb.name}
                                  title={thumb.name}
                                  loading="lazy"
                                  className="h-10 w-10 rounded-md border border-border object-cover"
                                  onError={() => hideBrokenImage(thumb.url)}
                                />
                              ))}
                              {runItems.length > runThumbs.length && (
                                <span className="rounded-md border border-border bg-muted px-2 py-1 text-[11px] font-semibold text-muted-foreground">
                                  +{runItems.length - runThumbs.length}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={allSelected ? "Avmarkera alla i skanningen" : "Markera alla i skanningen"}
                        disabled={!runItemIds.length}
                        onClick={() =>
                          setSelected((current) =>
                            allSelected
                              ? current.filter((id) => !runItemIds.includes(id))
                              : Array.from(new Set([...current, ...runItemIds])),
                          )
                        }
                      >
                        <SquareCheckBig
                          className={cn("h-4 w-4", allSelected ? "text-primary" : "text-muted-foreground")}
                        />
                      </Button>
                      {isAdminViewer && <ScanRunDebugPanel runId={run.id} label={run.label} />}
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Byt namn"
                        onClick={() => setRenaming({ id: run.id, label: run.label })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Radera skanning"
                        className="text-muted-foreground"
                        onClick={() => setPendingRunDelete({ id: run.id, label: run.label })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {renaming?.id === run.id && (
                      <div className="flex gap-2 border-t border-border p-4">
                        <Input
                          value={renaming.label}
                          autoFocus
                          onChange={(e) => setRenaming({ id: run.id, label: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && renaming.label.trim()) {
                              renameMutation.mutate({ runId: run.id, label: renaming.label.trim() });
                            }
                            if (e.key === "Escape") setRenaming(null);
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={!renaming.label.trim() || renameMutation.isPending}
                          onClick={() =>
                            renameMutation.mutate({ runId: run.id, label: renaming.label.trim() })
                          }
                        >
                          Spara
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                          Avbryt
                        </Button>
                      </div>
                    )}

                    {open && (
                      <ul className="divide-y divide-border border-t border-border">
                        {runItems.map((item) => (
                          <ItemCard key={item.id} item={item} />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <p className="mt-6 text-xs text-muted-foreground">
            Värdena är våra uppskattningar av vad prylarna kan säljas för begagnat i Sverige — inte
            en officiell värdering, och du bestämmer alltid pris och text själv. Bilder från en film
            är bara till för att du ska känna igen prylen; fota prylen separat när du ska sälja den.
          </p>
        </>
      )}
      {selected.length > 0 && (
        <div className="sticky bottom-20 z-30 mt-6 sm:bottom-6">
          <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur">
            <p className="pl-1 text-sm font-medium">
              {selected.length} {selected.length === 1 ? "pryl vald" : "prylar valda"}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={selected.length < 2}
                onClick={() => setPendingMerge(true)}
              >
                <Layers className="h-4 w-4" />
                Slå ihop
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-2 text-destructive"
                onClick={() => setPendingBulkDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
                Ta bort
              </Button>
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                Avbryt
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={pendingBulkDelete} onOpenChange={setPendingBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Ta bort {selected.length} {selected.length === 1 ? "pryl" : "prylar"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Prylarna och deras bilder tas bort från dina listor. Det går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                bulkDeleteMutation.mutate(selected);
              }}
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingMerge} onOpenChange={setPendingMerge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slå ihop {selected.length} prylar till en?</AlertDialogTitle>
            <AlertDialogDescription>
              Vi behåller den med högst uppskattat värde, samlar alla bilder på den och sätter
              värdet till hela intervallet. Bra när samma sak dykt upp flera gånger i filmen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              disabled={mergeMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                mergeMutation.mutate(selected);
              }}
            >
              Slå ihop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ItemDialog />
      {lightbox && (
        <Dialog open onOpenChange={(open) => !open && setLightbox(null)}>
          <DialogContent className="max-w-3xl border-0 bg-background p-3 sm:p-4">
            <DialogHeader>
              <DialogTitle className="pr-6 text-left text-base">{lightbox.name}</DialogTitle>
            </DialogHeader>
            <img
              src={lightbox.urls[lightbox.index]}
              alt={`Bild ${lightbox.index + 1} på ${lightbox.name}`}
              className="max-h-[70dvh] w-full rounded-xl object-contain"
            />
            {lightbox.urls.length > 1 && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setLightbox((l) =>
                      l
                        ? { ...l, index: (l.index - 1 + l.urls.length) % l.urls.length }
                        : l,
                    )
                  }
                >
                  Föregående
                </Button>
                <span className="text-xs text-muted-foreground">
                  {lightbox.index + 1} / {lightbox.urls.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setLightbox((l) => (l ? { ...l, index: (l.index + 1) % l.urls.length } : l))
                  }
                >
                  Nästa
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      <SoldPriceDialog
        open={!!pendingSold}
        itemName={pendingSold?.name ?? ""}
        suggestedPrice={
          pendingSold
            ? (pendingSold.valuationValueMid ??
              Math.round(((pendingSold.valueLow ?? 0) + (pendingSold.valueHigh ?? 0)) / 2) ??
              null)
            : null
        }
        saving={statusMutation.isPending}
        onCancel={() => setPendingSold(null)}
        onConfirm={(soldPrice) => {
          if (!pendingSold) return;
          statusMutation.mutate({ itemId: pendingSold.id, status: "sold", soldPrice });
          setPendingSold(null);
        }}
      />
      {testFeatures && valuating && (
        <DeepValuationDialog
          item={valuating}
          onClose={() => setValuating(null)}
          onDone={() => void queryClient.invalidateQueries({ queryKey: ["my-scans"] })}
          onCreateAd={(valuation) => {
            const item = items.find((i) => i.id === valuating.id);
            try {
              sessionStorage.setItem(
                "sellavo.scan-prefill",
                JSON.stringify({
                  name: item?.name ?? valuating.name,
                  note: item?.note,
                  category: item?.category,
                  price: valuation.valueMid || valuation.valueLow || undefined,
                  title: valuation.adTitle || undefined,
                  description: valuation.adText || undefined,
                  imageUrls: item?.imageUrls ?? [],
                  fromDeepValuation: true,
                  jump: true,
                }),
              );
            } catch {
              // Utan sessionStorage får man fylla i själv.
            }
            setValuating(null);
            setDetail(null);
            void navigate({ to: "/skapa", replace: false });
          }}
        />
      )}


      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort ”{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Prylen och dess bilder tas bort från din lista. Det går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) {
                  deleteItemMutation.mutate(pendingDelete.id);
                  if (detail?.id === pendingDelete.id) setDetail(null);
                }
                setPendingDelete(null);
              }}
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingRunDelete}
        onOpenChange={(open) => !open && setPendingRunDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort ”{pendingRunDelete?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Hela skanningen och alla prylar i den tas bort. Det går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingRunDelete) deleteRunMutation.mutate(pendingRunDelete.id);
                setPendingRunDelete(null);
              }}
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>

  );
}
